import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import dayjs, { Dayjs, OpUnitType } from 'shared/dayjs'
import { getDateFormatBasedOnLocale } from '../date-time-pickers/dateTimePickersUtils'
import {
  ButtonClean,
  ButtonPrimary,
  Modal,
  Box,
  Flex
} from 'front-end/src/components'
import {
  getDateRangeById,
  PredefinedDateRangeItem,
  predefinedDateRanges
} from '../../utils/predefinedDateRanges'
import { notification } from '../notifications/Notifications'
import { RadioList } from '../form/radio/RadioList'
import DatePicker from '../date-time-pickers/DatePicker'
import { useLooopTheme } from '../../../public-api/hooks/useLooopTheme'
import styles from './dateRangeFilterModal.styles'
import IconArrowRight from '../icon/IconArrowRight'

interface DateFilterParams {
  period: PredefinedDateRangeItem | null
  to: Date
  from: Date
}

interface DateRangeFilterModalProps extends DateFilterParams {
  isOpened: boolean
  closeModal: () => void
  onFilterSubmit: (params: DateFilterParams) => void
}

export const DateRangeFilterModal = ({
  isOpened,
  closeModal,
  onFilterSubmit,
  period,
  from: dateFrom,
  to: dateTo
}: DateRangeFilterModalProps) => {
  const { theme } = useLooopTheme()
  const [dateRange, setDateRange] = useState<PredefinedDateRangeItem | null>(
    period
  )
  const [from, setFrom] = useState<Dayjs>(() => dayjs(dateFrom))
  const [to, setTo] = useState<Dayjs>(() => dayjs(dateTo))
  const dateFormat = getDateFormatBasedOnLocale()

  const changeDateRangeWithRecalculation = React.useCallback(
    (rangeId: string) => {
      const parsedRangeId = +rangeId
      const usedDateRange = getDateRangeById(parsedRangeId)
      if (!usedDateRange) {
        setFrom(dayjs(dateFrom)) // use defaults
        setTo(dayjs(dateTo))
      } else {
        setFrom(
          dayjs()
            .subtract(usedDateRange.amount, usedDateRange.unit)
            .startOf('d')
        )
        setTo(dayjs().endOf('d'))
      }
      setDateRange(usedDateRange)
    },
    []
  )

  const handleClose = () => closeModal()

  return (
    <Modal
      show={isOpened}
      onHide={handleClose}
      controls={
        <>
          <ButtonClean onClick={handleClose}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            onClick={() => {
              let newFrom = from
              let newTo = to

              // we don't need disabling, error message, or whatever else
              if (to && from && to.isBefore(from)) {
                ;[newFrom, newTo] = [newTo, newFrom]
              }

              if (dateRange) {
                newFrom = dayjs()
                  .subtract(dateRange.amount, dateRange.unit as OpUnitType)
                  .startOf('day')

                newTo = dayjs().endOf('day')
              } else {
                if (!dayjs(newFrom ?? undefined).isValid()) {
                  notification.error(t`Incorrect custom from date`)
                  return false
                }
                if (!dayjs(newTo ?? undefined).isValid()) {
                  notification.error(t`Incorrect custom to date`)
                  return false
                }
              }

              onFilterSubmit({
                period: dateRange,
                from: newFrom.toDate(),
                to: newTo.toDate()
              })
              closeModal()
            }}
          >
            <Trans>Set filter</Trans>
          </ButtonPrimary>
        </>
      }
      header={t`Date range`}
      subHeader={t`Filter to include only activity within the specified date range:`}
      className={styles}
    >
      <Box
        borderRadius={theme.radii.base}
        bgColor={theme.colors.grey.lightest}
        maxH="calc(80vh - 160px)"
        overflow="auto"
      >
        <RadioList
          options={predefinedDateRanges}
          name={'dateRange'}
          onChange={changeDateRangeWithRecalculation}
          value={dateRange?.value ?? null}
        />
      </Box>
      <Box mt="24px" mb="4px" fontSize="12px" color={theme.colors.grey.mid}>
        <Trans>Or select a custom date range</Trans>:
      </Box>
      <Flex alignItems="center">
        <DatePicker
          inputProps={{
            ['aria-label']: t`from`
          }}
          className="from"
          value={from ? dayjs(from).format(dateFormat) : ''}
          onChange={(value) => {
            setFrom(dayjs(value.toString()).startOf('day'))
            setDateRange(null)
          }}
        />
        <IconArrowRight m="0 16px" />
        <DatePicker
          inputProps={{
            ['aria-label']: t`to`
          }}
          className="to"
          isValidDate={
            from
              ? (current) =>
                  dayjs(current).isAfter(
                    dayjs(from).startOf('day').subtract(1, 'day')
                  )
              : undefined
          }
          value={to ? dayjs(to).format(dateFormat) : ''}
          onChange={(value) => {
            setTo(dayjs(value.toString()).endOf('day'))
            setDateRange(null)
          }}
        />
      </Flex>
    </Modal>
  )
}
